import { Flex, Box, Text, Button, Collapse, useDisclosure, Divider, Link, Tooltip } from '@chakra-ui/react';
import { ReactComponent as ArrowDown } from 'assets/arrow-down-icon.svg';
import { ReactComponent as SuccessIcon } from 'assets/success-icon.svg';
import { ReactComponent as ArrowsIconGreen } from 'assets/arrows-icon-green.svg';
import { ReactComponent as ContractIcon } from 'assets/contract-icon-green.svg';
import { ReactComponent as SettingsIcon } from 'assets/settings-icon-green.svg';
import { ReactComponent as GreenWalletIcon } from 'assets/green-wallet.svg';
import { ReactComponent as PlusIconGreen } from 'assets/plus-icon-green.svg';
import { ReactComponent as CommentIcon } from 'assets/comment-icon.svg';

import copyToClipboard from 'copy-to-clipboard';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { signCheck, addManualSignature, executeTokenAssociation } from '../../../api/multisigAPI';
import { useToast } from '../../../hooks/useToast';
import { getWalletErrorMessage } from '../../../utils/walletErrors';
import { getExplorerUrl } from '../../../utils/explorerUrl';
import { clearKey } from '../../../signing/KeyManager';
import { useSigningConfirmation } from '../../../providers/SigningConfirmation';
import { GlobalContext } from '../../../providers/Global';
import { signScheduledTransaction, signTokenAssociateTransaction } from '../../../services/TransactionService';
import { buildCustomStrategy } from '../../../signing/useStrategy';
import { verifyProposal } from '../../../services/verifyProposal';
import {
  TransactionStatusEnum,
  TransactionType,
  OutgoingTransaction,
  isTransferParams,
  isContractInteractionParams,
  isTokenTransferParams,
  isTokenAssociationParams,
  isFundParams,
  isThresholdUpdateParams,
  ThresholdUpdateParams,
} from '../../../types/transactions';
import { TokenBalance } from '../../../types/token';
import { ModalType, SCREEN_SM } from 'utils/constants';
import { useMediaQuery } from 'react-responsive';
import { format } from 'date-fns';
import AddressBookModal from 'components/Modals/AddressBookModal';
import TransferDetails from './details/TransferDetails';
import TokenTransferDetails from './details/TokenTransferDetails';
import ContractDetails from './details/ContractDetails';
import TokenAssociationDetails from './details/TokenAssociationDetails';
import ThresholdUpdateDetails from './details/ThresholdUpdateDetails';
import FundDetails from './details/FundDetails';
import ProgressTimeline from './details/ProgressTimeline';
import { executingGlow, scheduledGlow, newTransactionGlow } from '../../styles/MultisigAccounts';

const statusGlowMap: Partial<Record<number, any>> = {
  [TransactionStatusEnum.Waiting]: newTransactionGlow,
  [TransactionStatusEnum.Executed]: executingGlow,
  [TransactionStatusEnum.AwaitingExecution]: scheduledGlow,
};

const styles = {
  status: (isMobile: boolean, color?: string) => {
    if (isMobile && color) {
      return {
        position: 'relative',
        '&:before': {
          content: "''",
          position: "absolute",
          inset: 0,
          right: "unset",
          width: '2px',
          backgroundColor: color,
        }
      }
    }
    return {}
  }
};

interface OwnProps {
  transaction: TransactionType | OutgoingTransaction;
  highlight?: boolean;
}

const OutgoingTransactionItem = ({ transaction, highlight }: OwnProps) => {
  const {
    _id,
    type,
    createdAt,
    expires_at,
    wait_for_expiry,
    description,
    signatures,
    signatures_count,
    signed,
    status,
    schedule_id,
    tx_id,
    network,
    required_signatures,
    params,
    signed_tx_bytes,
    wallet_account_id,
  } = transaction;
  const { walletId, multisigAccountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const expandId = searchParams.get('expand');
  const shouldAutoExpand = expandId === _id;
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: shouldAutoExpand });
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shouldAutoExpand && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Clear the expand param so it doesn't re-trigger
      setSearchParams((prev) => {
        prev.delete('expand');
        return prev;
      }, { replace: true });
    }
    // Only run when shouldAutoExpand changes — not on every searchParams reference change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoExpand]);
  const {
    connection: { accountId, hashconnectConnectorInstance },
    currentAccount,
    connectedNetwork,
    openModal,
    setSelectedToken,
    triggerRefresh,
  } = useContext(GlobalContext);
  const { showToast } = useToast();
  const { requestSignConfirmation } = useSigningConfirmation();

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });

  const { isOpen: isSaveOpen, onOpen: onSaveOpen, onClose: onSaveClose } = useDisclosure();
  const [saveAddress, setSaveAddress] = useState('');

  const openTokenDetails = (tokenId: string, symbol?: string, name?: string, decimals?: number) => {
    const token: TokenBalance = {
      token_id: tokenId,
      symbol: symbol || '',
      name: name || '',
      decimals: decimals || 0,
      balance: 0,
      type: 'FUNGIBLE_COMMON',
    };
    const fullToken = currentAccount.tokens?.find(t => t.token_id === tokenId);
    setSelectedToken(fullToken || token);
    openModal(ModalType.TokenDetailsModal);
  };

  const handleCopy = (text: string) => {
    copyToClipboard(text);
    showToast('Copied to clipboard', 'success', undefined, 'top');
  };

  const handleSaveAddress = (address: string) => {
    setSaveAddress(address);
    onSaveOpen();
  };

  // Determine transaction type
  const isContractInteraction = type === 'ContractInteraction' && isContractInteractionParams(params);
  const isTransfer = isTransferParams(params);
  const isTokenTransfer = type === 'TokenTransfer' && isTokenTransferParams(params);
  const isTokenAssociation = type === 'TokenAssociation' && isTokenAssociationParams(params);
  const isFund = isFundParams(params, type);

  const isMultisig = useMemo(() => {
    return [
      'Transfer',
      'TokenTransfer',
      'ContractInteraction',
      'ThresholdUpdate',
      'TokenAssociation'
    ].includes(type);
  }, [type]);

  const displayAmount = useMemo(() => {
    if (isContractInteraction) {
      return params.payableAmount ? `${params.payableAmount} HBAR` : '0 HBAR';
    }
    if (isTokenTransfer && isTokenTransferParams(params)) {
      return `${params.amount} ${params.tokenSymbol}`;
    }
    if (isTokenAssociation) return '—';
    if (isFund && isFundParams(params)) return `${params.amount} HBAR`;
    if (isTransfer) return `${params.amount} HBAR`;
    if (type === 'ThresholdUpdate' && isThresholdUpdateParams(params, type)) {
      const oldSigners = params.oldSigners || currentAccount.keys || [];
      const newSignerIds = new Set((params.newSigners || []).map((s) => s.accountId));
      const oldIds = new Set(oldSigners.map((s) => s.accountId));
      const added = Array.from(newSignerIds).filter((id) => !oldIds.has(id)).length;
      const removed = Array.from(oldIds).filter((id) => !newSignerIds.has(id)).length;
      const oldThreshold = params.oldThreshold ?? currentAccount.threshold;
      const thresholdChanged = params.newThreshold != null && params.newThreshold !== oldThreshold;

      const parts: string[] = [];
      if (added > 0) parts.push(`${added} added`);
      if (removed > 0) parts.push(`${removed} removed`);
      if (thresholdChanged && parts.length > 0) return 'Complex update';
      if (parts.length > 0) return parts.join(', ');
      if (thresholdChanged) return 'Threshold change';
      return 'Config update';
    }
    return '0 HBAR';
  }, [isContractInteraction, isTransfer, type, params, currentAccount]);

  const displayActionName = useMemo(() => {
    if (isContractInteraction) return 'Contract Call';
    if (isTokenTransfer) return 'Token Transfer';
    if (isTokenAssociation) return 'Token Associate';
    if (isFund) return 'Fund';
    if (type === 'ThresholdUpdate') return 'Wallet Update';
    return 'Transfer';
  }, [isContractInteraction, isTokenTransfer, isTokenAssociation, isFund, type]);

  const displayTarget = useMemo(() => {
    if (type === 'Transfer' && isTransferParams(params)) {
      return params.toAccountId;
    }
    if (isTokenTransfer && isTokenTransferParams(params)) return params.toAccountId;
    if (isContractInteraction && isContractInteractionParams(params)) return params.contractId;
    if (isTokenAssociation && isTokenAssociationParams(params)) return params.tokenId;
    if (type === 'ThresholdUpdate' && isThresholdUpdateParams(params, type)) {
      const threshold = params.newThreshold ?? currentAccount.threshold;
      const signersCount = params.newSigners?.length ?? currentAccount.keys?.length;
      return `→ ${threshold}/${signersCount}`;
    }
    return '—';
  }, [isTransfer, isTokenTransfer, isContractInteraction, isTokenAssociation, isFund, type, params, currentAccount]);

  const TypeIcon = useMemo(() => {
    if (isContractInteraction) return ContractIcon;
    if (isTokenTransfer) return ArrowsIconGreen;
    if (isTokenAssociation) return PlusIconGreen;
    if (isFund) return GreenWalletIcon;
    if (type === 'ThresholdUpdate') return SettingsIcon;
    return ArrowsIconGreen;
  }, [isContractInteraction, isTokenTransfer, isTokenAssociation, isFund, type]);

  const effectiveDescription = useMemo(() => {
    if (!description || description === 'description') return null;
    if (isContractInteraction && description.startsWith('Contract call:')) return null;
    return description;
  }, [description, isContractInteraction]);

  const descriptionSnippet = useMemo(() => {
    if (!effectiveDescription) return null;
    return effectiveDescription.length > 50 ? `${effectiveDescription.slice(0, 50)}...` : effectiveDescription;
  }, [effectiveDescription]);

  const createdBy = useMemo(() => {
    if (!signatures?.length) return null;
    const firstSig = signatures[0];
    const signerInfo = (currentAccount.keys || []).find(
      (k: any) => k.accountId === firstSig.accountId || k.key === firstSig.key,
    );
    return {
      name: signerInfo?.name || '',
      accountId: firstSig.accountId || signerInfo?.accountId || '',
    };
  }, [signatures, currentAccount.keys]);

  const descriptionAuthor = effectiveDescription ? createdBy : null;

  const relevantSigners = useMemo(() => {
    if (type === 'ThresholdUpdate' && isThresholdUpdateParams(params, type) && params.newSigners) {
      // Union of old and new signers — removed signers are still valid until execution
      const seen = new Set<string>();
      const result: { accountId: string; name: string; key: string }[] = [];
      const oldSigners = params.oldSigners || currentAccount.keys || [];
      for (const s of oldSigners) {
        if (!seen.has(s.accountId)) {
          seen.add(s.accountId);
          result.push({ accountId: s.accountId, name: s.name || '', key: '' });
        }
      }
      for (const s of params.newSigners) {
        if (!seen.has(s.accountId)) {
          seen.add(s.accountId);
          result.push({ accountId: s.accountId, name: s.name || '', key: s.publicKey || '' });
        }
      }
      return result;
    }
    return (currentAccount.keys || []).map((k) => ({ ...k, key: k.key || '' }));
  }, [type, params, currentAccount.keys]);

  const sign = async () => {
    try {
      // Check on the mirror node that the proposal does what it shows before
      // anything is signed: what executes is the schedule (or, for token
      // associations, the stored bytes), not the description or params. It
      // is checked against the wallet in the page address and the connected
      // network, never against the proposal's own claims.
      const verdict = await verifyProposal(transaction, {
        walletAccountId: multisigAccountId ?? '',
        network: connectedNetwork!,
        signerAccountId: accountId,
      });
      if (!verdict.ok) {
        // Handled below like the network's own SCHEDULE_ALREADY_EXECUTED
        if (verdict.alreadyExecuted) throw new Error('SCHEDULE_ALREADY_EXECUTED');
        showToast(`Not signed: ${verdict.reason}`, 'error', undefined, 'top-right');
        return;
      }

      const isCustom = connectedNetwork?.startsWith('custom-');

      if (isCustom) {
        const confirmed = await requestSignConfirmation({ type: 'Sign Transaction', items: verdict.items });
        if (!confirmed) return;
      }

      // Build strategy AFTER confirmation (key is freshly decrypted)
      const strategy = buildCustomStrategy(connectedNetwork, accountId);

      // Same test as the check's: any TokenAssociation signs the checked bytes
      if (type === 'TokenAssociation' && signed_tx_bytes) {
        let result;
        if (strategy) {
          result = await signTokenAssociateTransaction(strategy, signed_tx_bytes);
        } else {
          result = await hashconnectConnectorInstance.signTokenAssociateTransaction(signed_tx_bytes, connectedNetwork!, accountId);
        }
        if (walletId && result) {
          await addManualSignature(walletId, _id, {
            accountId,
            publicKey: result.publicKey,
            signedTxBytes: result.signedTxBytes,
          });
          showToast('Successfully signed', 'success', undefined, 'top-right');
          triggerRefresh();
        }
      } else {
        let signedTx;
        if (strategy) {
          signedTx = await signScheduledTransaction(strategy, schedule_id);
        } else {
          signedTx = await hashconnectConnectorInstance.signScheduledTransaction(schedule_id, accountId, connectedNetwork!);
        }
        if (walletId && signedTx) {
          const txIdStr = 'txId' in signedTx ? signedTx.txId : signedTx.transactionId?.toString();
          try {
            await signCheck(walletId, txIdStr, _id, accountId);
          } catch {
            // signCheck may fail if the transaction already executed — still a success
          }
          showToast('Successfully signed', 'success', undefined, 'top-right');
          triggerRefresh();
          // Delayed refresh to catch status update from eagerReconcile
          // (mirror node needs a few seconds to ingest the execution)
          setTimeout(() => triggerRefresh(), 5000);
          setTimeout(() => triggerRefresh(), 12000);
        }
      }
    } catch (e: unknown) {
      const errorMsg = e instanceof Error ? e.message : String(e);
      if (errorMsg.includes('SCHEDULE_ALREADY_EXECUTED')) {
        // Schedule already executed on Hedera (threshold was met).
        // Treat as success — just refresh to show updated status.
        showToast('Transaction already executed on the network!', 'success', undefined, 'top-right');
        triggerRefresh();
        setTimeout(() => triggerRefresh(), 5000);
      } else {
        console.error('Signing failed:', e);
        showToast(getWalletErrorMessage(e, 'Failed to sign transaction'), 'error', undefined, 'top-right');
      }
    } finally {
      clearKey();
    }
  };

  const executeManualTransaction = async () => {
    if (!walletId) return;
    try {
      await executeTokenAssociation(walletId, _id);
      showToast('Transaction executed successfully', 'success', undefined, 'top-right');
      window.location.reload();
    } catch (e) {
      console.log(e);
      showToast('Failed to execute transaction', 'error', undefined, 'top-right');
    }
  };

  const canExecuteManually = useMemo(() => {
    if (!isTokenAssociation || !signed_tx_bytes) return false;
    if (status !== TransactionStatusEnum.AwaitingExecution) return false;
    const now = Math.floor(Date.now() / 1000);
    const validStart = expires_at - 180;
    return now >= validStart && now < expires_at;
  }, [isTokenAssociation, signed_tx_bytes, status, expires_at]);

  const statusColor = useMemo(() => {
    switch (status) {
      case TransactionStatusEnum.Success:
        return '#1EC843'
      case TransactionStatusEnum.Executed:
        return '#38A169'
      case TransactionStatusEnum.Expired:
      case TransactionStatusEnum.Failed:
      case TransactionStatusEnum.Deleted:
        return '#E53E3E'
      case TransactionStatusEnum.Waiting:
        return '#ED8936'
      case TransactionStatusEnum.AwaitingExecution:
        return '#3182CE'
      default:
        return
    }
  }, [status]);

  const statusDisplayText = useMemo(() => {
    if (status === TransactionStatusEnum.AwaitingExecution) return 'Scheduled';
    if (status === TransactionStatusEnum.Executed) return 'Executing';
    return TransactionStatusEnum[status];
  }, [status]);

  const explorerUrl = useMemo(() => {
    const isExecuted = status === TransactionStatusEnum.Success || status === TransactionStatusEnum.Failed;

    const formatTxIdForExplorer = (txId: string): string => {
      // Strip ?scheduled or other suffixes
      const cleanId = txId.split('?')[0];
      const match = cleanId.match(/^(\d+\.\d+\.\d+)@(\d+)\.(\d+)$/);
      if (match) {
        const [, acct, seconds, nanos] = match;
        return `${acct}-${seconds}-${nanos}`;
      }
      return cleanId;
    };

    if (isExecuted && tx_id) {
      return getExplorerUrl(network, 'transaction', formatTxIdForExplorer(tx_id));
    }
    if (isTokenAssociation && signed_tx_bytes) {
      if (isTokenAssociationParams(params)) {
        return getExplorerUrl(network, 'token', params.tokenId);
      }
    }
    if (!schedule_id && tx_id) {
      return getExplorerUrl(network, 'transaction', formatTxIdForExplorer(tx_id));
    }
    return getExplorerUrl(network, 'schedule', schedule_id);
  }, [network, status, tx_id, schedule_id, isTokenAssociation, signed_tx_bytes, params]);

  const dateFormat = useCallback((date: Date) => {
    return isScreenSmall
      ? format(date, 'dd MMM yyyy')
      : new Date(date).toLocaleTimeString([], {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
  }, [isScreenSmall]);

  // Render the type-specific detail component
  const renderDetails = () => {
    if (isContractInteraction && isContractInteractionParams(params)) {
      return (
        <ContractDetails
          contractId={params.contractId}
          contractMethod={params.contractMethod}
          contractParams={params.params}
          gas={params.gas}
          payableAmount={params.payableAmount}
          onCopy={handleCopy}
          onSaveAddress={handleSaveAddress}
          expiresAt={expires_at}
          waitForExpiry={wait_for_expiry}
          isMultisig={isMultisig}
          createdBy={createdBy || undefined}
        />
      );
    }
    if (isTokenTransfer && isTokenTransferParams(params)) {
      return (
        <TokenTransferDetails
          amount={params.amount}
          tokenSymbol={params.tokenSymbol}
          tokenId={params.tokenId}
          tokenDecimals={params.tokenDecimals}
          toAccountId={params.toAccountId}
          onCopy={handleCopy}
          onSaveAddress={handleSaveAddress}
          onTokenClick={() => openTokenDetails(params.tokenId, params.tokenSymbol, '', params.tokenDecimals)}
          expiresAt={expires_at}
          waitForExpiry={wait_for_expiry}
          isMultisig={isMultisig}
          createdBy={createdBy || undefined}
        />
      );
    }
    if (isTokenAssociation && isTokenAssociationParams(params)) {
      return (
        <TokenAssociationDetails
          tokenId={params.tokenId}
          tokenName={params.tokenName}
          tokenSymbol={params.tokenSymbol}
          onCopy={handleCopy}
          onTokenClick={() => openTokenDetails(params.tokenId, params.tokenSymbol, params.tokenName)}
          expiresAt={expires_at}
          waitForExpiry={wait_for_expiry}
          isMultisig={isMultisig}
          isManual={!!signed_tx_bytes}
          createdBy={createdBy || undefined}
        />
      );
    }
    if (isFund && isFundParams(params)) {
      return (
        <FundDetails
          amount={params.amount}
          transactionId={params.transactionId}
          createdBy={createdBy || undefined}
        />
      );
    }
    if (type === 'ThresholdUpdate' && isThresholdUpdateParams(params, type)) {
      const newSigners = params.newSigners || [];
      const oldSigners = params.oldSigners || currentAccount.keys || [];
      const newSignerIds = new Set(newSigners.map((s) => s.accountId));
      const oldSignerIds = new Set(oldSigners.map((s) => s.accountId));

      const added = newSigners
        .filter((s) => !oldSignerIds.has(s.accountId))
        .map((s) => ({ accountId: s.accountId, name: s.name }));
      const removed = oldSigners
        .filter((s) => !newSignerIds.has(s.accountId))
        .map((s) => ({ accountId: s.accountId, name: s.name }));

      const effectiveNewThreshold = params.newThreshold ?? currentAccount.threshold ?? 1;
      const oldThreshold = params.oldThreshold ?? currentAccount.threshold ?? 1;
      const thresholdChanged = params.newThreshold != null && params.newThreshold !== oldThreshold;

      return (
        <ThresholdUpdateDetails
          newThreshold={effectiveNewThreshold}
          signersCount={newSigners.length || oldSigners.length}
          expiresAt={expires_at}
          waitForExpiry={wait_for_expiry}
          isMultisig={isMultisig}
          addedSigners={added.length > 0 ? added : undefined}
          removedSigners={removed.length > 0 ? removed : undefined}
          thresholdChanged={thresholdChanged}
          oldThreshold={oldThreshold}
          createdBy={createdBy || undefined}
        />
      );
    }
    if (isTransfer && isTransferParams(params)) {
      return (
        <TransferDetails
          amount={params.amount}
          toAccountId={params.toAccountId}
          fromAccountId={wallet_account_id}
          createdBy={createdBy || undefined}
          onCopy={handleCopy}
          onSaveAddress={handleSaveAddress}
          expiresAt={expires_at}
          waitForExpiry={wait_for_expiry}
          isMultisig={isMultisig}
        />
      );
    }
    return null;
  };

  return (
    <Box ref={itemRef}>
      <Flex
        minH={14}
        px={4}
        py={0}
        borderTopRadius={isScreenSmall ? 0 : 8}
        onClick={onToggle}
        cursor={'pointer'}
        alignItems="center"
        borderY="1px solid #F6F6F6"
        sx={{
          ...styles.status(isScreenSmall, statusColor),
          ...(highlight && statusGlowMap[status] ? statusGlowMap[status] : {}),
        }}
      >
        {/* Type Icon */}
        {!isScreenSmall && (
          <Box w={10} display="flex" alignItems="center" justifyContent="center">
            <Box
              w={8}
              h={8}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="primary"
              borderRadius="full"
            >
              <TypeIcon style={{ width: 14, height: 14 }} />
            </Box>
          </Box>
        )}

        {/* Action + Description */}
        <Box flex={3} display="flex" alignItems="center" gap={1.5}>
          <Text size="xs" isTruncated fontWeight="500">
            {displayActionName}
          </Text>
          {descriptionSnippet && (
            <Tooltip label={descriptionSnippet} placement="top" hasArrow>
              <Box flexShrink={0} color="#1EC843">
                <CommentIcon width={12} height={12} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Amount / Method */}
        <Box flex={2}>
          <Text size="xs" isTruncated>
            {isContractInteraction && isContractInteractionParams(params)
              ? params.contractMethod
              : displayAmount
            }
          </Text>
        </Box>

        {/* To / Target */}
        {!isScreenSmall && (
          <Box flex={2}>
            <Tooltip label={displayTarget} placement="top" hasArrow isDisabled={displayTarget === '—'}>
              <Text size="xs" isTruncated color="blackAlpha.700">
                {displayTarget}
              </Text>
            </Tooltip>
          </Box>
        )}

        {/* Expires */}
        <Box flex={2} display="flex" alignItems="center" gap={1}>
          {isMultisig && (
            <Text size="xs" isTruncated>
              {dateFormat(new Date(expires_at * 1000))}
            </Text>
          )}
          {wait_for_expiry && (
            <Box
              px={1.5}
              py={0.5}
              bg="orange.100"
              borderRadius={4}
              title="Will execute only at expiration time"
            >
              <Text size="xs" color="orange.700" fontWeight="500" fontSize="10px">
                ⏱
              </Text>
            </Box>
          )}
        </Box>

        {/* Threshold */}
        {!isScreenSmall && (
          <Box flex={1}>
            {isMultisig && (
              <Text size="xs" ml={3}>
                {signatures_count} / {required_signatures}
              </Text>
            )}
          </Box>
        )}

        {/* Status + Arrow */}
        {!isScreenSmall && (
          <Box w={130} display="flex" alignItems="center">
            <Text
              mr={1}
              w={78}
              h="24px"
              ml="auto"
              size="xs"
              color="white"
              align="center"
              borderRadius={6}
              variant="bodyBold"
              bg={statusColor}
              lineHeight="24px"
            >
              {statusDisplayText}
            </Text>
            <Box
              transition="transform 0.2s"
              transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
            >
              <ArrowDown />
            </Box>
          </Box>
        )}
      </Flex>
      <Collapse in={isOpen} transition={{ exit: { delay: 0.1 }, enter: { duration: 0.2 } }}>
        <Flex
          direction="column"
          px={4}
          py={6}
          borderRadius={8}
          gap={5}
          bg="primary"
        >
          <Flex
            gap={4}
            direction={isScreenSmall ? 'column' : 'row'}
          >
            <Flex
              flex={3}
              flexDirection="column"
              gap={4}
            >
              <Flex direction="column" gap={1}>
                <Text size="xs">Details</Text>
                {renderDetails()}
              </Flex>

              {effectiveDescription && (
                <Flex direction="column" gap={1.5}>
                  {descriptionAuthor && (
                    <Flex alignItems="center" gap={1.5} pl={1}>
                      <Box flexShrink={0} color="#1EC843">
                        <CommentIcon width={12} height={12} />
                      </Box>
                      <Text size="xs" color="blackAlpha.500" fontWeight="500">
                        {descriptionAuthor.name
                          ? `${descriptionAuthor.name} (${descriptionAuthor.accountId})`
                          : descriptionAuthor.accountId}
                      </Text>
                    </Flex>
                  )}
                  <Box
                    position="relative"
                    bg="white"
                    borderRadius={8}
                    px={3}
                    py={2.5}
                    ml={1}
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: '-6px',
                      left: '12px',
                      width: '10px',
                      height: '10px',
                      bg: 'white',
                      transform: 'rotate(45deg)',
                    }}
                  >
                    <Text size="xs" color="blackAlpha.700" wordBreak="break-word" lineHeight="1.5">
                      {effectiveDescription}
                    </Text>
                  </Box>
                </Flex>
              )}
            </Flex>

            {isMultisig && (
              <>
                <Divider
                  h="inherit"
                  border="1px solid rgba(0, 0, 0, 0.16)"
                  orientation={isScreenSmall ? 'horizontal' : 'vertical'}
                  mt={isScreenSmall ? 2 : 0}
                />

                <Box flex={2}>
                  <ProgressTimeline
                    createdAt={createdAt}
                    status={status}
                    statusColor={statusColor}
                    signaturesCount={signatures_count}
                    requiredSignatures={required_signatures}
                    signatures={signatures}
                    signers={relevantSigners}
                    expiresAt={expires_at}
                    isScreenSmall={isScreenSmall}
                    signerGroups={
                      type === 'ThresholdUpdate' && isThresholdUpdateParams(params, type)
                        ? [
                            {
                              label: 'Current Signers',
                              threshold: params.oldThreshold ?? currentAccount.threshold ?? 1,
                              signers: (params.oldSigners || currentAccount.keys || []).map((s) => ({
                                accountId: s.accountId,
                                name: s.name || '',
                                key: '',
                              })),
                            },
                            {
                              label: 'New Configuration',
                              threshold: params.newThreshold ?? currentAccount.threshold ?? 1,
                              signers: (params.newSigners || []).map((s) => ({
                                accountId: s.accountId,
                                name: s.name || '',
                                key: s.publicKey || '',
                              })),
                            },
                          ]
                        : undefined
                    }
                  />
                </Box>
              </>
            )}
          </Flex>

          <Flex justifyContent="space-between" alignItems="center">
            {explorerUrl && (
              <Link
                href={explorerUrl}
                isExternal
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                gap={1.5}
                px={isScreenSmall ? 2 : 3}
                py={1.5}
                fontSize="xs"
                fontWeight="500"
                color="#2d8a45"
                bg="#f0faf2"
                borderRadius={6}
                _hover={{ bg: '#e5f7ea', color: '#1a6e32' }}
                transition="all 0.15s"
              >
                View in Explorer
              </Link>
            )}
            {isMultisig && (
              canExecuteManually ? (
                <Button
                  w={160}
                  onClick={executeManualTransaction}
                  borderRadius={6}
                  variant="primary"
                  colorScheme="green"
                >
                  Execute
                </Button>
              ) : signed ? (
                <Flex align="center" gap={2} mr={3}>
                  <SuccessIcon width={20} height={20} />
                  <Text size="xs" variant="bodyBold">
                    You've signed this transaction
                  </Text>
                </Flex>
              ) : (
                <Button
                  w={160}
                  onClick={sign}
                  borderRadius={6}
                  variant="primary"
                  isDisabled={status !== TransactionStatusEnum.Waiting}
                >
                  Sign
                </Button>
              )
            )}
          </Flex>
        </Flex>
      </Collapse>

      <AddressBookModal
        isOpen={isSaveOpen}
        onClose={onSaveClose}
        onSave={() => { }}
        prefillAddress={saveAddress}
        userAccountId={accountId}
      />
    </Box>
  );
};

export default OutgoingTransactionItem;
